import type { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

type Tone = 'neutral' | 'primary' | 'success' | 'danger' | 'warning' | 'info';

const toneMap: Record<Tone, ReturnType<typeof css>> = {
  neutral: css`
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.textSecondary};
  `,
  primary: css`
    background: ${({ theme }) => theme.colors.primarySoft};
    color: ${({ theme }) => theme.colors.primaryHover};
  `,
  success: css`
    background: ${({ theme }) => theme.colors.successSoft};
    color: ${({ theme }) => theme.colors.success};
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.dangerSoft};
    color: ${({ theme }) => theme.colors.danger};
  `,
  warning: css`
    background: ${({ theme }) => theme.colors.warningSoft};
    color: ${({ theme }) => theme.colors.warning};
  `,
  info: css`
    background: ${({ theme }) => theme.colors.infoSoft};
    color: ${({ theme }) => theme.colors.info};
  `,
} as const;

export const Badge = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 600;
  ${({ $tone = 'neutral' }: { $tone?: Tone }) => toneMap[$tone]}
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span<{ $size?: number }>`
  display: inline-block;
  width: ${({ $size = 18 }) => $size}px;
  height: ${({ $size = 18 }) => $size}px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const AlertBox = styled.div<{ $tone?: Tone }>`
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.65;
  ${({ $tone = 'info' }: { $tone?: Tone }) => toneMap[$tone]}
`;

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  return <AlertBox $tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>{children}</AlertBox>;
}

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyTitle = styled.p`
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const EmptyDesc = styled.p`
  max-width: 420px;
  font-size: 14px;
  line-height: 1.75;
`;

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <EmptyWrap>
      <EmptyTitle>{title}</EmptyTitle>
      {description && <EmptyDesc>{description}</EmptyDesc>}
      {action}
    </EmptyWrap>
  );
}
